import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
// Without a timezone plugin FullCalendar coerces Date events to UTC, which
// pushes late-evening classes (after 8 PM ET) into the next day's column.
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import { collection, documentId, getDocs, doc, updateDoc, arrayUnion, query, where, Timestamp, writeBatch } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { Squad, User } from '../../../types';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import '../styles/SquadCalendar.css';
import ConfirmationModal from './ConfirmationModal';
import EditEventModal from './EditEventModal';
import { DEFAULT_TZ, formatTimeInZone } from '../../../shared/utils/timezone';
import { useAuth } from '../../auth/contexts/AuthContext';
import {
  displayCurriculum,
  displaySeason,
  formatListingDateRange,
  formatListingTime,
  listingNeedsStaffing,
  StaffSquadListing,
} from '../utils/staffSquadListing';
import { isDemoExperienceSquad } from '../utils/curriculum';
import {
  cancelSubRequestForSquadEvent,
  claimSubOpportunity,
  dateKeyForDateInZone,
  quarterKeyForDateInZone,
  requestSubForSquadEvent,
  restoreOriginalTeacherForSquadEvent,
  type SubRequestReason,
} from '../services/subbing';
import { resolveEffectiveEventStaffing, isSupportingAssignmentCovered, type ActiveCoverage } from '../utils/effectiveEventStaffing';
import { addEventParticipant, removeEventParticipant, type EventParticipant } from '../services/eventParticipants';
import { eventStaffRoleLabel, type EventSupportingStaff } from '../utils/eventSupportingStaff';

type SupportedState = 'Massachusetts' | 'Illinois';

function squadStatusIsInactiveCalendar(status: unknown): boolean {
  const st = String(status || '').trim().toLowerCase();
  return st === 'canceled' || st === 'cancelled';
}

function parseSupportedStateFromAddress(address: string | null | undefined): SupportedState | null {
  if (!address) return null;
  const normalized = String(address).trim();
  if (!normalized) return null;

  if (/\b(Massachusetts|MA)\b/i.test(normalized)) return 'Massachusetts';
  if (/\b(Illinois|IL)\b/i.test(normalized)) return 'Illinois';
  return null;
}

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  squadId: string;
  mainTeacherId?: string;
  supportingTeacherIds?: string[];
  mainTeacherOverridden?: boolean;
  subStatus?: 'none' | 'requested' | 'assigned' | 'canceled';
  subRequestedByTeacherId?: string | null;
  subRequestedByTeacherName?: string | null;
  subOriginalMainTeacherId?: string | null;
  activeCoverage?: ActiveCoverage[];
  assignedMainTeacherId?: string;
  assignedSupportingTeacherIds?: string[];
  eventParticipants?: EventParticipant[];
  supportingStaff?: EventSupportingStaff;
}

function mapCalendarEvents(snapshot: any): CalendarEvent[] {
  return snapshot.docs.map((eventDoc: any) => {
    const data = eventDoc.data();
    const effective = resolveEffectiveEventStaffing(data);
    return {
      id: eventDoc.id,
      title: data.title,
      start: data.start.toDate(),
      end: data.end.toDate(),
      squadId: data.squadId,
      mainTeacherId: effective.effectiveLeadTeacherId || undefined,
      supportingTeacherIds: effective.effectiveSupportingTeacherIds,
      assignedMainTeacherId: data.mainTeacherId,
      assignedSupportingTeacherIds: data.supportingTeacherIds || [],
      activeCoverage: Array.isArray(data.activeCoverage) ? data.activeCoverage : undefined,
      eventParticipants: Array.isArray(data.eventParticipants) ? data.eventParticipants : [],
      supportingStaff: data.supportingStaff || {},
      mainTeacherOverridden: data.mainTeacherOverridden,
      subStatus: (data.subStatus || 'none') as any,
      subRequestedByTeacherId: typeof data.subRequestedByTeacherId === 'string' ? data.subRequestedByTeacherId : (data.subRequestedByTeacherId ?? null),
      subRequestedByTeacherName: typeof data.subRequestedByTeacherName === 'string' ? data.subRequestedByTeacherName : null,
      subOriginalMainTeacherId: typeof data.subOriginalMainTeacherId === 'string' ? data.subOriginalMainTeacherId : null,
      allDay: false,
    } as CalendarEvent;
  });
}

function surveyEventIds(events: CalendarEvent[]): Set<string> {
  const teacherFirstEvents = new Map<string, CalendarEvent>();
  [...events].sort((a, b) => a.start.getTime() - b.start.getTime()).forEach(event => {
    if (event.mainTeacherId && !teacherFirstEvents.has(event.mainTeacherId)) teacherFirstEvents.set(event.mainTeacherId, event);
  });
  const ids = new Set<string>();
  teacherFirstEvents.forEach(event => {
    if (!event.supportingTeacherIds?.length) ids.add(event.id);
  });
  return ids;
}

/**
 * Extra calendar item rendered alongside squad class events — used for partner
 * events and no-class days on the partner Calendar tab. All-day items should use
 * date-only strings (end exclusive) so they land on the right cells regardless
 * of the display timezone.
 */
export interface AuxCalendarEvent {
  id: string;
  title: string;
  kind: 'partner_event' | 'no_class';
  start: string | Date;
  end?: string | Date;
  allDay: boolean;
  converted?: boolean;
  source?: 'manual' | 'ai';
}

interface SquadCalendarProps {
  onDateClick?: (date: Date) => void;
  onEventDelete?: (eventId: string) => void;
  onEventUpdate?: (event: any) => void;
  refreshKey?: number;
  teachers?: User[];
  squads?: Squad[];
  eventClickAction?: 'navigate' | 'edit';
  /** When true, only events belonging to the provided squads are shown. */
  scopeToInitialSquads?: boolean;
  initialView?: 'dayGridMonth' | 'dayGridWeek';
  onSquadOpen?: (squadId: string) => void;
  /** Simplified embedded view: month only, with no filters, timezone picker, Today, or view switcher. */
  compactMonthOnly?: boolean;
  /** Partner events and no-class days to render alongside class events. */
  auxEvents?: AuxCalendarEvent[];
  onAuxEventClick?: (auxEventId: string) => void;
}

const TIMEZONE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'America/New_York', label: 'Eastern (America/New_York)' },
  { value: 'America/Chicago', label: 'Central (America/Chicago)' },
  { value: 'America/Denver', label: 'Mountain (America/Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (America/Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific (America/Los_Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (America/Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Pacific/Honolulu)' },
];

function tzShort(timeZone: string): string {
  const tz = String(timeZone || '').trim();
  const map: Record<string, string> = {
    'America/New_York': 'ET',
    'America/Chicago': 'CT',
    'America/Denver': 'MT',
    'America/Phoenix': 'AZ',
    'America/Los_Angeles': 'PT',
    'America/Anchorage': 'AK',
    'Pacific/Honolulu': 'HI',
  };
  return map[tz] || tz;
}

const SquadCalendar: React.FC<SquadCalendarProps> = ({ onDateClick, onEventDelete, onEventUpdate, refreshKey, teachers, squads: initialSquads, eventClickAction = 'navigate', scopeToInitialSquads = false, initialView = 'dayGridWeek', onSquadOpen, compactMonthOnly = false, auxEvents, onAuxEventClick }) => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [squads, setSquads] = useState<Squad[]>(initialSquads || []);
  const [locationsById, setLocationsById] = useState<Record<string, any>>({});
  const [filteredEvents, setFilteredEvents] = useState<CalendarEvent[]>([]);
  const [selectedSquads, setSelectedSquads] = useState<string[]>([]);
  const [selectedTeachers, setSelectedTeachers] = useState<string[]>([]);
  const [selectedStates, setSelectedStates] = useState<SupportedState[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [isAdminEventActionsOpen, setIsAdminEventActionsOpen] = useState(false);
  const [adminEventActionMode, setAdminEventActionMode] = useState<'actions' | 'requestSub' | 'fillSub'>('actions');
  const [fillSubTeacherId, setFillSubTeacherId] = useState('');
  const [adminSubReason, setAdminSubReason] = useState<SubRequestReason>('schedule_conflict');
  const [adminSubTeacherId, setAdminSubTeacherId] = useState('');
  const [adminSubNote, setAdminSubNote] = useState('');
  const [adminSubBusy, setAdminSubBusy] = useState(false);
  const [adminSubError, setAdminSubError] = useState('');
  const [isCancelSubConfirmOpen, setIsCancelSubConfirmOpen] = useState(false);
  const [surveyClassEventIds, setSurveyClassEventIds] = useState<Set<string>>(new Set());
  const [calendarTimeZone, setCalendarTimeZone] = useState<string>(DEFAULT_TZ);
  const [pendingTimeZone, setPendingTimeZone] = useState<string | null>(null);
  const [isTzConfirmOpen, setIsTzConfirmOpen] = useState(false);
  const [hasManualTzOverride, setHasManualTzOverride] = useState(false);
  const [loadedTeachers, setLoadedTeachers] = useState<User[]>([]);
  const [discoveryPreview, setDiscoveryPreview] = useState<{
    listing: StaffSquadListing;
    start?: Date;
    end?: Date;
  } | null>(null);
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const fetchGenerationRef = useRef(0);

  const accountTimeZone = String(userData?.timeZone || DEFAULT_TZ).trim() || DEFAULT_TZ;
  const calendarTeachers = teachers === undefined ? loadedTeachers : teachers;
  const squadsById = useMemo(
    () => new Map(squads.map((squad) => [squad.id, squad] as const)),
    [squads],
  );
  const teachersById = useMemo(
    () => new Map(calendarTeachers.map((teacher) => [teacher.id, teacher] as const)),
    [calendarTeachers],
  );

  useEffect(() => {
    if (teachers !== undefined || !initialSquads?.length) {
      if (teachers !== undefined) setLoadedTeachers([]);
      return;
    }
    const ids = Array.from(new Set(initialSquads.flatMap((squad) => [
      ...(squad.teacherIds || []),
      String(squad.regularTeacher || '').trim(),
    ]).filter(Boolean)));
    let canceled = false;
    const chunks = Array.from({ length: Math.ceil(ids.length / 30) }, (_, index) => ids.slice(index * 30, index * 30 + 30));
    Promise.all(chunks.map((chunk) => getDocs(query(
      collection(db, 'users'),
      where(documentId(), 'in', chunk),
    )))).then((snapshots) => {
      if (!canceled) setLoadedTeachers(snapshots.flatMap((snapshot) => snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as User))));
    }).catch((error) => console.error('Error loading calendar teachers:', error));
    return () => { canceled = true; };
  }, [initialSquads, teachers]);

  const dayKey = useCallback((d: Date): string => {
    try {
      return formatTimeInZone(d, calendarTimeZone, 'yyyy-MM-dd');
    } catch {
      // Fallback to ISO date key (UTC)
      return d?.toISOString?.().split('T')?.[0] || '';
    }
  }, [calendarTimeZone]);

  const unassignedDayKeys = useMemo(() => {
    const set = new Set<string>();
    for (const ev of filteredEvents) {
      if (!ev?.start || Number.isNaN(ev.start.getTime?.())) continue;
      if (!ev.mainTeacherId) set.add(dayKey(ev.start));
    }
    return set;
  }, [filteredEvents, dayKey]);

  const finalDayKeyBySquadId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const ev of events) {
      if (!ev?.start || Number.isNaN(ev.start.getTime?.())) continue;
      const squadId = String(ev.squadId || '').trim();
      if (!squadId) continue;
      const k = dayKey(ev.start);
      if (!k) continue;
      const prev = map[squadId];
      if (!prev || k > prev) map[squadId] = k;
    }
    return map;
  }, [events, dayKey]);

  const firstDayKeyBySquadId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const ev of events) {
      if (!ev?.start || Number.isNaN(ev.start.getTime?.())) continue;
      const squadId = String(ev.squadId || '').trim();
      if (!squadId) continue;
      const k = dayKey(ev.start);
      if (!k) continue;
      const prev = map[squadId];
      if (!prev || k < prev) map[squadId] = k;
    }
    return map;
  }, [events, dayKey]);

  const lessonProgressByEventId = useMemo(() => {
    const grouped = new Map<string, CalendarEvent[]>();
    events.forEach((event) => {
      const squadId = String(event.squadId || '').trim();
      if (!squadId) return;
      grouped.set(squadId, [...(grouped.get(squadId) || []), event]);
    });
    const progress = new Map<string, string>();
    grouped.forEach((squadEvents) => {
      const ordered = [...squadEvents].sort((a, b) => a.start.getTime() - b.start.getTime() || a.id.localeCompare(b.id));
      ordered.forEach((event, index) => progress.set(event.id, `${index + 1}/${ordered.length}`));
    });
    return progress;
  }, [events]);

  useEffect(() => {
    // Default to the account timezone on load (non-persistent override).
    // If the user hasn't manually overridden during this session, keep it in sync.
    if (!hasManualTzOverride) {
      setCalendarTimeZone(accountTimeZone);
    }
    // If account timezone changes while modal is open, cancel the pending switch.
    setPendingTimeZone(null);
    setIsTzConfirmOpen(false);
  }, [accountTimeZone, hasManualTzOverride]);

  useEffect(() => {
    if (initialSquads) {
      setSquads(initialSquads);
    }
  }, [initialSquads]);

  

  useEffect(() => {
    let newFilteredEvents = events;

    // Never show calendar blocks for canceled squads (covers stale fetches or lag
    // before squadEvents are deleted). Archived squads remain visible.
    newFilteredEvents = newFilteredEvents.filter((event) => {
      const sid = String(event?.squadId || '').trim();
      if (!sid) return true;
      const squad = squadsById.get(sid);
      if (!squad) return true;
      return !squadStatusIsInactiveCalendar((squad as any)?.status);
    });

    if (selectedSquads.length > 0) {
      newFilteredEvents = newFilteredEvents.filter(event => selectedSquads.includes(event.squadId));
    }

    if (selectedTeachers.length > 0) {
      const selectedTeacherSet = new Set(selectedTeachers);
      newFilteredEvents = newFilteredEvents.filter(event => {
        const teacherIds = [
          event.mainTeacherId,
          ...(event.supportingTeacherIds || []),
          ...(event.eventParticipants || []).map(item => item.userId),
        ].filter(Boolean) as string[];
        return teacherIds.some(teacherId => selectedTeacherSet.has(teacherId));
      });
    }

    if (selectedStates.length > 0) {
      const allowed = new Set<SupportedState>(selectedStates);
      newFilteredEvents = newFilteredEvents.filter(event => {
        const squad = squadsById.get(event.squadId);
        const locationId = String(squad?.locationId || '').trim();
        const canonicalLocation = locationId ? locationsById[locationId] : null;
        const address = canonicalLocation?.address
          || canonicalLocation?.location?.address
          || squad?.location?.address
          || (squad as any)?.address;
        const state = parseSupportedStateFromAddress(address);
        if (!state) return false;
        return allowed.has(state);
      });
    }

    setFilteredEvents(newFilteredEvents);
  }, [events, selectedSquads, selectedTeachers, selectedStates, squadsById, locationsById]);

  const fetchEvents = useCallback(async () => {
    const generation = ++fetchGenerationRef.current;
    setLoading(true);

    // Supporting metadata is useful for labels and filters, but it must never
    // delay the event blocks themselves. Load it alongside the visible range.
    const supportingDataPromise = (async () => {
      const [locationsResult, squadsResult] = await Promise.all([
        compactMonthOnly
          ? Promise.resolve(null)
          : getDocs(collection(db, 'locations')),
        initialSquads
          ? (!scopeToInitialSquads && !compactMonthOnly
            ? Promise.allSettled([
                getDocs(collection(db, 'staffSquadListings')),
                getDocs(collection(db, 'publicSquads')),
              ])
            : Promise.resolve(null))
          : getDocs(collection(db, 'squads')),
      ]);

      if (generation !== fetchGenerationRef.current) return;

      if (locationsResult) {
        setLocationsById(Object.fromEntries(
          locationsResult.docs.map((locationDoc) => [
            locationDoc.id,
            { id: locationDoc.id, ...locationDoc.data() },
          ]),
        ));
      }

      if (!initialSquads && squadsResult && 'docs' in squadsResult) {
        setSquads(squadsResult.docs.map((item) => ({ ...item.data(), id: item.id } as Squad)));
      } else if (initialSquads && Array.isArray(squadsResult)) {
        const [staffResult, publicResult] = squadsResult;
        const discovered = new Map<string, Squad>();
        if (publicResult.status === 'fulfilled') {
          publicResult.value.docs.forEach((item) => {
            discovered.set(item.id, { id: item.id, ...item.data() } as Squad);
          });
        }
        if (staffResult.status === 'fulfilled') {
          staffResult.value.docs.forEach((item) => {
            discovered.set(item.id, {
              ...(discovered.get(item.id) || {}),
              id: item.id,
              ...item.data(),
            } as Squad);
          });
        }
        initialSquads.forEach((squad) => discovered.set(squad.id, squad));
        setSquads(Array.from(discovered.values()));
      }
    })().catch((error) => console.warn('Could not load calendar supporting data:', error));

    try {
      const eventsCollection = collection(db, 'squadEvents');
      const now = new Date();
      // Include a one-week gutter so month/week views at the boundary render
      // immediately without waiting for the archive request.
      const visibleStart = new Date(now.getFullYear(), now.getMonth(), -6);
      const visibleEnd = new Date(now.getFullYear(), now.getMonth() + 1, 8);
      const visibleSnapshot = await getDocs(query(
        eventsCollection,
        where('start', '>=', Timestamp.fromDate(visibleStart)),
        where('start', '<', Timestamp.fromDate(visibleEnd)),
      ));
      if (generation !== fetchGenerationRef.current) return;
      let eventsData = mapCalendarEvents(visibleSnapshot);
      if (scopeToInitialSquads && initialSquads) {
        const allowedSquadIds = new Set(initialSquads.map((squad) => squad.id));
        eventsData = eventsData.filter((event) => allowedSquadIds.has(event.squadId));
      }
      setEvents(eventsData);
      setSurveyClassEventIds(surveyEventIds(eventsData));

      // The visible calendar is usable now. Warm the complete archive without
      // keeping the loading overlay up or blocking interaction.
      window.setTimeout(() => {
        void getDocs(eventsCollection).then((archiveSnapshot) => {
          if (generation !== fetchGenerationRef.current) return;
          let archiveEvents = mapCalendarEvents(archiveSnapshot);
          if (scopeToInitialSquads && initialSquads) {
            const allowedSquadIds = new Set(initialSquads.map((squad) => squad.id));
            archiveEvents = archiveEvents.filter(event => allowedSquadIds.has(event.squadId));
          }
          setEvents(archiveEvents);
          setSurveyClassEventIds(surveyEventIds(archiveEvents));
        }).catch(error => console.warn('Background calendar archive load failed:', error));
      }, 0);

    } catch (error) {
      console.error("Error fetching squad events:", error);
    } finally {
      if (generation === fetchGenerationRef.current) setLoading(false);
    }
    void supportingDataPromise;
  }, [compactMonthOnly, initialSquads, scopeToInitialSquads]);

  useEffect(() => {
    fetchEvents();
    return () => {
      // Ignore late responses from an obsolete refresh or an unmounted calendar.
      fetchGenerationRef.current += 1;
    };
  }, [refreshKey, fetchEvents]);

  const handleDateClick = (arg: { date: Date }) => {
    if (onDateClick) {
      onDateClick(arg.date);
    }
  };

  const handleEventClick = (clickInfo: any) => {
    if (clickInfo.jsEvent.target.classList.contains('delete-event-btn')) {
      return; // Don't process click if the delete button was the target
    }

    // Partner events / no-class days are owned by the embedding page.
    if (clickInfo.event.extendedProps?.aux) {
      onAuxEventClick?.(String(clickInfo.event.extendedProps.auxId || ''));
      return;
    }

    if (userData?.isAdmin === true) {
      setSelectedEvent(clickInfo.event);
      setAdminEventActionMode('actions');
      setAdminSubReason('schedule_conflict');
      {
        // Value encodes teacher AND role ("uid::lead" / "uid::co_teacher") so a
        // dual-role teacher's two assignments stay distinct, and an event with
        // co-teachers but no lead can still get coverage requested.
        const storedLead = String(clickInfo.event.extendedProps?.assignedMainTeacherId || clickInfo.event.extendedProps?.mainTeacherId || '');
        const storedSupport: string[] = (clickInfo.event.extendedProps?.assignedSupportingTeacherIds || []).map(String).filter(Boolean);
        setAdminSubTeacherId(storedLead ? `${storedLead}::lead` : (storedSupport[0] ? `${storedSupport[0]}::co_teacher` : ''));
      }
      setAdminSubNote('');
      setAdminSubError('');
      setIsAdminEventActionsOpen(true);
    } else if (eventClickAction === 'edit' && onEventUpdate) {
      setSelectedEvent(clickInfo.event);
      setIsEditModalOpen(true);
    } else {
      const squadId = clickInfo.event.extendedProps.squadId;
      if (squadId) {
        const eventTeacherIds = [
          clickInfo.event.extendedProps.mainTeacherId,
          ...(clickInfo.event.extendedProps.supportingTeacherIds || []),
        ].filter(Boolean);
        const isAssigned = Boolean(
          userData?.isAdmin
          || initialSquads?.some((squad) => squad.id === squadId)
          || (currentUser && eventTeacherIds.includes(currentUser.uid)),
        );
        const isTeacherDiscovery = !scopeToInitialSquads && userData?.role === 'teacher';
        if (isTeacherDiscovery && !isAssigned) {
          const listing = squadsById.get(squadId);
          if (listing) {
            setDiscoveryPreview({
              listing: listing as unknown as StaffSquadListing,
              start: clickInfo.event.start || undefined,
              end: clickInfo.event.end || undefined,
            });
          }
          return;
        }
        if (onSquadOpen) onSquadOpen(squadId);
        else navigate(`/app/squads/${squadId}`);
      }
    }
  };

  const [adminSubTeacherIdOnly, adminSubSelectedRole] = adminSubTeacherId.includes('::')
    ? [adminSubTeacherId.split('::')[0], adminSubTeacherId.split('::')[1]]
    : [adminSubTeacherId, ''];
  const selectedAdminEventTeacher = adminSubTeacherIdOnly
    ? teachersById.get(adminSubTeacherIdOnly)
    : null;
  const selectedAdminEventSquad = selectedEvent?.extendedProps?.squadId
    ? squadsById.get(selectedEvent.extendedProps.squadId)
    : null;
  const selectedClaimedCoverage = Array.isArray(selectedEvent?.extendedProps?.activeCoverage)
    ? selectedEvent.extendedProps.activeCoverage.find((coverage: ActiveCoverage) => coverage.status === 'claimed')
    : null;
  const selectedOriginalTeacherId = String(
    selectedClaimedCoverage?.requestedTeacherId
      || selectedEvent?.extendedProps?.subOriginalMainTeacherId
      || '',
  ).trim();
  const selectedOriginalTeacherName = teachersById.get(selectedOriginalTeacherId)?.displayName
    || selectedEvent?.extendedProps?.subRequestedByTeacherName
    || 'the original teacher';
  const selectedAssignedTeacherId = String(selectedEvent?.extendedProps?.assignedMainTeacherId || '').trim();
  const selectedResultingTeacherName = teachersById.get(selectedAssignedTeacherId)?.displayName || 'the assigned lead teacher';
  const selectedReturnsToOriginal = Boolean(selectedOriginalTeacherId)
    && selectedAssignedTeacherId === selectedOriginalTeacherId;

  const requestAdminSub = async () => {
    if (!selectedEvent?.id || !selectedAdminEventTeacher?.id || !selectedAdminEventSquad?.id) return;
    if (!adminSubNote.trim()) {
      setAdminSubError('Add teaching notes so the substitute knows how to prepare for this class.');
      return;
    }
    const start = selectedEvent.start as Date | null;
    const end = selectedEvent.end as Date | null;
    if (!start || !end) {
      setAdminSubError('This event is missing its start or end time.');
      return;
    }
    const timeZone = String(selectedAdminEventSquad.timezone || calendarTimeZone || DEFAULT_TZ);
    setAdminSubBusy(true);
    setAdminSubError('');
    try {
      await requestSubForSquadEvent({
        eventId: selectedEvent.id,
        squadId: selectedAdminEventSquad.id,
        requestedByTeacherId: selectedAdminEventTeacher.id,
        requestedRole: adminSubSelectedRole === 'co_teacher'
          ? 'co_teacher'
          : adminSubSelectedRole === 'lead'
            ? 'lead'
            : selectedEvent.extendedProps?.assignedMainTeacherId === selectedAdminEventTeacher.id ? 'lead' : 'co_teacher',
        classTitle: selectedEvent.title || selectedAdminEventSquad.title || 'Class',
        classStart: start,
        classEnd: end,
        quarterKey: quarterKeyForDateInZone(start, timeZone),
        dateKey: dateKeyForDateInZone(start, timeZone),
        reason: adminSubReason,
        note: adminSubNote.trim(),
        availabilityTimeZone: timeZone,
      });
      setIsAdminEventActionsOpen(false);
      setAdminEventActionMode('actions');
      await fetchEvents();
    } catch (error: any) {
      setAdminSubError(error?.message || 'Could not request a substitute.');
    } finally {
      setAdminSubBusy(false);
    }
  };

  const fillAdminSub = async () => {
    const squadId = String(selectedEvent?.extendedProps?.squadId || '').trim();
    if (!selectedEvent?.id || !squadId || !fillSubTeacherId) return;
    const hasOpenRequest = selectedEvent.extendedProps?.subStatus === 'requested';
    if (!hasOpenRequest && (!selectedAdminEventTeacher?.id || !selectedAdminEventSquad?.id)) return;
    if (!hasOpenRequest && !adminSubNote.trim()) {
      setAdminSubError('Add teaching notes so the substitute knows how to prepare for this class.');
      return;
    }
    setAdminSubBusy(true);
    setAdminSubError('');
    try {
      // For a class that does not already have a request, create the same V2
      // request the assigned teacher would have created, then immediately fill
      // it. Keeping both normal lifecycle transitions means notifications,
      // coverage packets, availability, and audit fields behave exactly like a
      // teacher request followed by a teacher claim.
      if (!hasOpenRequest) {
        const start = selectedEvent.start as Date | null;
        const end = selectedEvent.end as Date | null;
        if (!start || !end) throw new Error('This event is missing its start or end time.');
        const timeZone = String(selectedAdminEventSquad?.timezone || calendarTimeZone || DEFAULT_TZ);
        await requestSubForSquadEvent({
          eventId: selectedEvent.id,
          squadId,
          requestedByTeacherId: selectedAdminEventTeacher!.id,
          requestedRole: adminSubSelectedRole === 'co_teacher' ? 'co_teacher' : 'lead',
          classTitle: selectedEvent.title || selectedAdminEventSquad?.title || 'Class',
          classStart: start,
          classEnd: end,
          quarterKey: quarterKeyForDateInZone(start, timeZone),
          dateKey: dateKeyForDateInZone(start, timeZone),
          reason: adminSubReason,
          note: adminSubNote.trim(),
          availabilityTimeZone: timeZone,
        });
      }
      await claimSubOpportunity({
        eventId: selectedEvent.id,
        squadId,
        claimerTeacherId: fillSubTeacherId,
        substituteTeacherId: fillSubTeacherId,
      });
      setIsAdminEventActionsOpen(false);
      setAdminEventActionMode('actions');
      setFillSubTeacherId('');
      await fetchEvents();
    } catch (error: any) {
      setAdminSubError(error?.message || 'Could not assign the substitute.');
    } finally {
      setAdminSubBusy(false);
    }
  };

  const cancelAdminSub = async () => {
    const requestedByTeacherId = String(selectedEvent?.extendedProps?.subRequestedByTeacherId || '').trim();
    const squadId = String(selectedEvent?.extendedProps?.squadId || '').trim();
    if (!selectedEvent?.id || !requestedByTeacherId || !squadId) return;
    setAdminSubBusy(true);
    setAdminSubError('');
    try {
      await cancelSubRequestForSquadEvent({ eventId: selectedEvent.id, squadId, requestedByTeacherId });
      setIsCancelSubConfirmOpen(false);
      setIsAdminEventActionsOpen(false);
      await fetchEvents();
    } catch (error: any) {
      setIsCancelSubConfirmOpen(false);
      setAdminSubError(error?.message || 'Could not cancel the substitute request.');
    } finally {
      setAdminSubBusy(false);
    }
  };

  const restoreOriginalAdminTeacher = async () => {
    const squadId = String(selectedEvent?.extendedProps?.squadId || '').trim();
    if (!selectedEvent?.id || !squadId) return;
    setAdminSubBusy(true);
    setAdminSubError('');
    try {
      await restoreOriginalTeacherForSquadEvent({ eventId: selectedEvent.id, squadId });
      setIsAdminEventActionsOpen(false);
      await fetchEvents();
    } catch (error: any) {
      setAdminSubError(error?.message || 'Could not return this class to its original teacher.');
    } finally {
      setAdminSubBusy(false);
    }
  };

  const renderEventContent = (eventInfo: any) => {
    if (eventInfo.event.extendedProps?.aux) {
      const auxKind = eventInfo.event.extendedProps.auxKind;
      const auxStart = eventInfo.event.start;
      const auxEnd = eventInfo.event.end;
      const auxTimeRange = !eventInfo.event.allDay && auxStart && auxEnd
        ? `${formatTimeInZone(auxStart, calendarTimeZone, 'h:mm')}–${formatTimeInZone(auxEnd, calendarTimeZone, 'h:mm a')}`
        : '';
      const label = auxKind === 'no_class'
        ? 'No class'
        : eventInfo.event.extendedProps.auxSource === 'ai' ? 'Partner event · AI' : 'Partner event';
      return (
        <div className="event-content aux-event-content">
          <div className="aux-event-label">
            {label}
            {eventInfo.event.extendedProps.auxConverted && <span className="aux-event-converted-pill">converted</span>}
          </div>
          <div className="aux-event-title">{eventInfo.event.title}</div>
          {auxTimeRange && <div className="aux-event-time">{auxTimeRange}</div>}
        </div>
      );
    }

    const { start, end } = eventInfo.event;
    const { mainTeacherId, subStatus, squadId, activeCoverage } = eventInfo.event.extendedProps;
    const coverage = Array.isArray(activeCoverage) ? activeCoverage[0] : null;
    const coveredTeacher = coverage?.requestedTeacherId ? teachersById.get(coverage.requestedTeacherId) : null;
    const eventId = eventInfo.event.id;
    const isSurveyClass = surveyClassEventIds.has(eventId);
    const lessonProgress = lessonProgressByEventId.get(eventId) || '';

    // Get squad data for school name, program, and term
    const squad = squadsById.get(squadId);
    const squadLeadId = String(squad?.regularTeacher || squad?.leadTeacherId || '').trim();
    const assignedLeadId = String(eventInfo.event.extendedProps?.assignedMainTeacherId || '').trim();
    const isDateSpecificLead = Boolean(assignedLeadId)
      && assignedLeadId !== squadLeadId
      && !coverage;
    const canonicalLocation = squad?.locationId ? locationsById[squad.locationId] : null;
    const schoolName = canonicalLocation?.name
      || canonicalLocation?.location?.name
      || squad?.schoolName
      || squad?.locationName
      || squad?.location?.name
      || 'Location to be announced';
    
    // Determine program type from curriculumId
    const programType = displayCurriculum((squad || {}) as StaffSquadListing);
    const seasonName = displaySeason((squad || {}) as StaffSquadListing);

    const isFinalDay = (() => {
      const sid = String(squadId || '').trim();
      if (!sid || !start || Number.isNaN(start.getTime?.())) return false;
      const lastKey = finalDayKeyBySquadId[sid];
      if (!lastKey) return false;
      return dayKey(start) === lastKey;
    })();

    const isFirstDay = (() => {
      const sid = String(squadId || '').trim();
      if (!sid || !start || Number.isNaN(start.getTime?.())) return false;
      const firstKey = firstDayKeyBySquadId[sid];
      if (!firstKey) return false;
      return dayKey(start) === firstKey;
    })();
    const isDemo = isDemoExperienceSquad(squad);
    const isOneDaySquad = squad?.scheduleKind === 'single_day'
      || Boolean(squad?.schedule?.mainDate)
      || (isFirstDay && isFinalDay);

    const compactTeacherName = (name: string): string => {
      const parts = name.trim().split(/\s+/);
      return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : name;
    };

    // Use the same compact name format for every teacher on the calendar.
    const mainTeacher = mainTeacherId ? teachersById.get(mainTeacherId) : null;
    let teacherName = 'No Teacher Assigned';
    if (mainTeacher && mainTeacher.displayName) {
      const nameParts = mainTeacher.displayName.trim().split(/\s+/);
      if (nameParts.length > 1) {
        const firstName = nameParts[0];
        const lastNameInitial = nameParts[nameParts.length - 1][0];
        teacherName = `${firstName} ${lastNameInitial}.`;
      } else {
        teacherName = mainTeacher.displayName;
      }
    }

    // Format time range
    const startTime = start && !isNaN(start.getTime())
      ? formatTimeInZone(start, calendarTimeZone, 'h:mm')
      : null;
    const endTime = end && !isNaN(end.getTime())
      ? formatTimeInZone(end, calendarTimeZone, 'h:mm a')
      : null;
    const timeRange = (startTime && endTime)
      ? `${startTime}–${endTime} ${tzShort(calendarTimeZone)}`
      : '';

    return (
      <div className="event-content event-content-compact">
        <div className="event-details">
          <div className="event-row-school">{schoolName}</div>
          <div className="event-row-program">
            <span className="program-type">{programType}</span>
            {seasonName !== 'Season not set' && <span className="program-season"> · {seasonName}</span>}
          </div>
          <div className="event-row-teacher">
            Lead: {teacherName}
            {subStatus === 'requested' && <span className="sub-pill">needs sub</span>}
            {coverage?.status === 'claimed' && <span className="sub-pill">
              {coverage.requestedTeacherId === eventInfo.event.extendedProps?.assignedMainTeacherId
                ? `covering ${coverage.requestedRole === 'co_teacher' ? 'co-teacher' : 'lead'} for ${coveredTeacher?.displayName || 'teacher'}`
                : `${coverage.requestedRole === 'co_teacher' ? 'co-teacher' : 'lead'} substitute coverage`}
            </span>}
            {isDateSpecificLead && <span className="sub-pill">date-specific lead</span>}
            {isSurveyClass && subStatus !== 'requested' && <span className="oversight-pill">needs oversight</span>}
            {isDemo && <span className="demo-day-pill">demo</span>}
            {!isDemo && !isOneDaySquad && isFirstDay && <span className="first-day-pill">first day</span>}
            {!isDemo && !isOneDaySquad && isFinalDay && <span className="final-day-pill">final day</span>}
          </div>
          {(eventInfo.event.extendedProps.supportingTeacherIds || []).map((id: string) => {
            const staff = eventInfo.event.extendedProps.supportingStaff?.[id];
            const isCoLead = squad?.coTeachers?.some(item => item.teacherId === id);
            const name = teachersById.get(id)?.displayName || 'Supporting teacher';
            const role = staff ? eventStaffRoleLabel(staff.role) : isCoLead ? 'Co-lead teacher' : 'Co-teacher';
            return <div className="event-row-supporting" key={id} title={`${name} · ${role}`}>{name === 'Supporting teacher' ? name : compactTeacherName(name)} · {role}</div>;
          })}
          {(eventInfo.event.extendedProps.eventParticipants || []).filter((item: EventParticipant) => !(eventInfo.event.extendedProps.supportingTeacherIds || []).includes(item.userId)).map((item: EventParticipant) => (
            <div className="event-row-supporting" key={item.userId}>{compactTeacherName(teachersById.get(item.userId)?.displayName || '') || 'Team member'} · {eventStaffRoleLabel(item.role)}</div>
          ))}
          <div className="event-row-time-line"><span className="event-row-time">{timeRange}</span>{lessonProgress && <span className="event-row-lesson-progress" title={`Lesson ${lessonProgress}`}>{lessonProgress}</span>}</div>
        </div>
        {onEventDelete && (
          <button className="delete-event-btn" onClick={(e) => {
              e.stopPropagation();
              setSelectedEvent(eventInfo.event);
              setIsDeleteModalOpen(true);
          }}>
            ×
          </button>
        )}
      </div>
    );
  };

  const getPersonalizedMessage = () => {
    if (!selectedEvent) {
      return "Are you sure you want to delete this event?";
    }
    const title = selectedEvent.title;
    const date = selectedEvent.start.toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const time = selectedEvent.start.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit'
    });
    return `Are you sure you want to delete the "${title}" event on ${date} at ${time}? This action cannot be undone.`;
  }

  const dayCellDidMount = (arg: any) => {
    const dateStr = dayKey(arg.date);
    if (unassignedDayKeys.has(dateStr)) {
      const el = arg.el.querySelector('.fc-daygrid-day-top');
      if (el) {
        const subNeededEl = document.createElement('div');
        subNeededEl.className = 'substitute-needed-label';
        subNeededEl.innerText = 'Lead Teacher Needed';
        el.appendChild(subNeededEl);
      }
    }
  };

  const auxFcEvents = useMemo(() => (auxEvents || []).map((item) => ({
    id: `aux:${item.id}`,
    title: item.title,
    start: item.start,
    end: item.end,
    allDay: item.allDay,
    extendedProps: {
      aux: true,
      auxId: item.id,
      auxKind: item.kind,
      auxConverted: Boolean(item.converted),
      auxSource: item.source || 'manual',
    },
  })), [auxEvents]);

  const displayedEvents = useMemo(
    () => [...(filteredEvents as any[]), ...auxFcEvents],
    [filteredEvents, auxFcEvents],
  );

  const customEventSort = (a: any, b: any) => {
    if (a.start < b.start) return -1;
    if (a.start > b.start) return 1;
    if (a.title < b.title) return -1;
    if (a.title > b.title) return 1;
    return 0;
  }

  return (
    <div className="squad-calendar-wrapper">
      {!compactMonthOnly && <div className="calendar-header-info">
        <div className="timezone-indicator">
          <span className="timezone-indicator-label">📍 All times shown in:</span>
          <select
            className="timezone-select"
            value={pendingTimeZone ?? calendarTimeZone}
            onChange={(e) => {
              const next = String(e.target.value || '').trim() || DEFAULT_TZ;
              if (next === calendarTimeZone) return;
              setPendingTimeZone(next);
              setIsTzConfirmOpen(true);
            }}
            aria-label="Calendar timezone"
          >
            {TIMEZONE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>}
      {!compactMonthOnly && <div className="calendar-filters">
        <div className="filter-group">
          <label htmlFor="squad-filter">Filter by Squad:</label>
          <Select
            isMulti
            options={squads.map(s => ({ value: s.id, label: s.title }))}
            onChange={(selectedOptions) => {
              setSelectedSquads((selectedOptions || []).map(o => o.value));
            }}
            className="react-select-container"
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="teacher-filter">Filter by Teacher:</label>
          <Select
            isMulti
            options={calendarTeachers.map(t => ({ value: t.id, label: t.displayName || '' }))}
            onChange={(selectedOptions) => {
              setSelectedTeachers((selectedOptions || []).map(o => o.value));
            }}
            className="react-select-container"
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="state-filter">Filter by State:</label>
          <Select
            isMulti
            options={([
              { value: 'Massachusetts', label: 'Massachusetts' },
              { value: 'Illinois', label: 'Illinois' },
            ] as Array<{ value: SupportedState; label: string }>)}
            onChange={(selectedOptions) => {
              setSelectedStates((selectedOptions || []).map(o => o.value));
            }}
            className="react-select-container"
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
      </div>}
      {loading && <div className="calendar-loading">Loading events...</div>}
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin, momentTimezonePlugin]}
        initialView={initialView}
        timeZone={calendarTimeZone}
        headerToolbar={{
          left: compactMonthOnly ? 'prev,next' : 'prev,next today',
          center: 'title',
          right: compactMonthOnly ? '' : 'dayGridMonth,dayGridWeek'
        }}
        events={displayedEvents}
        dateClick={handleDateClick}
        eventClick={handleEventClick}
        eventContent={renderEventContent}
        eventOrder={customEventSort}
        selectable={true}
        eventClassNames={(arg) => {
          if (arg.event.extendedProps?.aux) {
            const auxClasses = [
              'aux-event',
              arg.event.extendedProps.auxKind === 'no_class' ? 'aux-event--no-class' : 'aux-event--partner-event',
            ];
            if (arg.event.extendedProps.auxConverted) auxClasses.push('aux-event--converted');
            return auxClasses;
          }
          const classes = ['squad-event'];
          const mainTeacherId = arg.event.extendedProps.mainTeacherId;
          const subStatus = (arg.event.extendedProps as any)?.subStatus;
          const eventId = arg.event.id;
          const isSurveyClass = surveyClassEventIds.has(eventId);
          
          if (!mainTeacherId) {
            classes.push('unassigned-event');
          }

          // Check if this is the current teacher's event (main or supporting)
          const supportingTeacherIds = arg.event.extendedProps.supportingTeacherIds || [];
          if (currentUser && (mainTeacherId === currentUser.uid || supportingTeacherIds.includes(currentUser.uid))) {
            classes.push('my-teacher-event');
          }

          if (subStatus === 'requested') {
            classes.push('sub-requested-event');
          }

          if (isSurveyClass) {
            classes.push('survey-class-event');
          }

          const squadId = arg.event.extendedProps.squadId;
          const squad = squadsById.get(squadId);

          // Demos and all other one-day squads use a single yellow treatment.
          // Multi-day squads retain separate first/final-day states.
          try {
            const sid = String(squadId || '').trim();
            const firstKey = sid ? firstDayKeyBySquadId[sid] : '';
            const lastKey = sid ? finalDayKeyBySquadId[sid] : '';
            const eventKey = arg.event.start ? dayKey(arg.event.start) : '';
            const isFirstDay = Boolean(firstKey && eventKey === firstKey);
            const isFinalDay = Boolean(lastKey && eventKey === lastKey);
            const isDemo = isDemoExperienceSquad(squad);
            const isOneDaySquad = squad?.scheduleKind === 'single_day'
              || Boolean(squad?.schedule?.mainDate)
              || Boolean(firstKey && lastKey && firstKey === lastKey);
            if (isDemo) {
              classes.push('demo-day-event');
            } else if (isOneDaySquad) {
              classes.push('one-day-event');
            } else {
              if (isFirstDay) classes.push('first-day-event');
              if (isFinalDay) classes.push('final-day-event');
              if (isFirstDay && !mainTeacherId) classes.push('unassigned-first-day-event');
            }
          } catch (_) {}

          // Mark event as live if the squad currently has an active live session
          if (squad && squad.liveSession) {
            const isPastDay = !!(arg.event.start && dayKey(arg.event.start) < dayKey(new Date()));
            classes.push(isPastDay ? 'needs-reflection-event' : 'live-event');
          }

          return classes;
        }}
        dayCellDidMount={dayCellDidMount}
      />
      {isAdminEventActionsOpen && selectedEvent && (
        <div className="calendar-admin-event-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !adminSubBusy) setIsAdminEventActionsOpen(false); }}>
          <section className="calendar-admin-event-modal" role="dialog" aria-modal="true" aria-labelledby="calendar-admin-event-title">
            <button type="button" className="calendar-admin-event-close" aria-label="Close event actions" disabled={adminSubBusy} onClick={() => setIsAdminEventActionsOpen(false)}>×</button>
            <span className="calendar-admin-event-eyebrow">Admin event actions</span>
            <h2 id="calendar-admin-event-title">{selectedEvent.title || 'Calendar event'}</h2>
            <div className="calendar-admin-event-summary">
              <span>{selectedEvent.start?.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
              <strong>{selectedAdminEventTeacher?.displayName || 'No lead teacher assigned'}</strong>
              {selectedEvent.extendedProps?.subStatus === 'requested' && <b>Sub requested</b>}
            </div>

            {adminSubError && <div className="calendar-admin-event-error">{adminSubError}</div>}

            {adminEventActionMode === 'actions' ? (
              <div className="calendar-admin-event-actions">
                {selectedEvent.extendedProps?.subStatus === 'assigned' && selectedOriginalTeacherId ? (
                  <button type="button" className="calendar-admin-event-action primary" disabled={adminSubBusy} onClick={() => void restoreOriginalAdminTeacher()}>
                    <strong>{selectedReturnsToOriginal ? `Return class to ${selectedOriginalTeacherName}` : 'End substitute coverage'}</strong>
                    <span>{selectedReturnsToOriginal ? 'End the current substitute coverage and restore the original teacher.' : `${selectedResultingTeacherName} will remain assigned to this class.`}</span>
                  </button>
                ) : selectedEvent.extendedProps?.subStatus === 'requested' ? (
                  <>
                    <button type="button" className="calendar-admin-event-action primary" disabled={adminSubBusy} onClick={() => { setFillSubTeacherId(''); setAdminSubError(''); setAdminEventActionMode('fillSub'); }}>
                      <strong>Fill this sub request</strong><span>Assign a substitute yourself instead of waiting for a claim.</span>
                    </button>
                    <button type="button" className="calendar-admin-event-action danger" disabled={adminSubBusy} onClick={() => setIsCancelSubConfirmOpen(true)}>
                      <strong>Cancel sub request</strong><span>Remove this coverage request and restore the teacher’s availability.</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" className="calendar-admin-event-action primary" disabled={!selectedAdminEventTeacher || adminSubBusy} onClick={() => { setFillSubTeacherId(''); setAdminSubError(''); setAdminEventActionMode('fillSub'); }}>
                      <strong>Assign a substitute</strong><span>{selectedAdminEventTeacher ? 'Choose a teacher and confirm coverage immediately.' : 'Assign a teacher to this class before arranging coverage.'}</span>
                    </button>
                    <button type="button" className="calendar-admin-event-action" disabled={!selectedAdminEventTeacher || adminSubBusy} onClick={() => setAdminEventActionMode('requestSub')}>
                      <strong>Request a sub for {selectedAdminEventTeacher?.displayName || 'this event'}</strong><span>{selectedAdminEventTeacher ? 'Create a coverage request on this teacher’s behalf.' : 'Assign a teacher to this class before requesting coverage.'}</span>
                    </button>
                  </>
                )}
                {eventClickAction === 'edit' && onEventUpdate && (
                  <button type="button" className="calendar-admin-event-action" onClick={() => { setIsAdminEventActionsOpen(false); setIsEditModalOpen(true); }}>
                    <strong>Edit event</strong><span>Change its teacher, date, or time.</span>
                  </button>
                )}
                {selectedEvent.extendedProps?.squadId && (
                  <button type="button" className="calendar-admin-event-action" onClick={() => { const squadId = selectedEvent.extendedProps.squadId; setIsAdminEventActionsOpen(false); if (onSquadOpen) onSquadOpen(squadId); else navigate(`/app/squads/${squadId}`); }}>
                    <strong>Open squad dashboard</strong><span>View the squad dashboard.</span>
                  </button>
                )}
              </div>
            ) : adminEventActionMode === 'fillSub' ? (
              <div className="calendar-admin-sub-form">
                {selectedEvent.extendedProps?.subStatus !== 'requested' && (
                  <>
                    <label>Covering for<select value={adminSubTeacherId} onChange={(event) => { setAdminSubTeacherId(event.target.value); setAdminSubError(''); }} disabled={adminSubBusy}>
                      {selectedEvent.extendedProps?.assignedMainTeacherId && <option value={`${selectedEvent.extendedProps.assignedMainTeacherId}::lead`}>{teachersById.get(selectedEvent.extendedProps.assignedMainTeacherId)?.displayName || 'Lead teacher'} — lead</option>}
                      {(selectedEvent.extendedProps?.assignedSupportingTeacherIds || []).map((teacherId: string) => <option key={`${teacherId}::co_teacher`} value={`${teacherId}::co_teacher`}>{teachersById.get(teacherId)?.displayName || 'Co-teacher'} — co-teacher</option>)}
                    </select></label>
                    <label>Reason<select value={adminSubReason} onChange={(event) => setAdminSubReason(event.target.value as SubRequestReason)} disabled={adminSubBusy}><option value="schedule_conflict">Schedule conflict</option><option value="illness">Illness</option><option value="travel">Travel</option><option value="other">Other</option></select></label>
                    <label>Teaching notes<textarea rows={4} value={adminSubNote} onChange={(event) => setAdminSubNote(event.target.value)} placeholder="What should the substitute know to teach this class successfully?" disabled={adminSubBusy} /></label>
                  </>
                )}
                <label>Substitute<select value={fillSubTeacherId} onChange={(event) => { setFillSubTeacherId(event.target.value); setAdminSubError(''); }} disabled={adminSubBusy}>
                  <option value="">Choose substitute</option>
                  {[...calendarTeachers]
                    .filter((teacher) => teacher.id !== String(selectedEvent.extendedProps?.subRequestedByTeacherId || selectedAdminEventTeacher?.id || ''))
                    .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))
                    .map((teacher) => <option key={teacher.id} value={teacher.id}>{teacher.displayName}</option>)}
                </select></label>
                <p className="calendar-admin-sub-note">Assigning records the same request-and-claim workflow a teacher would use, including the coverage packet and confirmations.</p>
                <div className="calendar-admin-sub-form-actions"><button type="button" disabled={adminSubBusy} onClick={() => setAdminEventActionMode('actions')}>Back</button><button type="button" className="primary" disabled={adminSubBusy || !fillSubTeacherId || (selectedEvent.extendedProps?.subStatus !== 'requested' && !adminSubNote.trim())} onClick={() => void fillAdminSub()}>{adminSubBusy ? 'Assigning…' : 'Assign sub'}</button></div>
              </div>
            ) : (
              <div className="calendar-admin-sub-form">
                <label>Teacher and role<select value={adminSubTeacherId} onChange={(event) => setAdminSubTeacherId(event.target.value)} disabled={adminSubBusy}>
                  {selectedEvent.extendedProps?.assignedMainTeacherId && <option value={`${selectedEvent.extendedProps.assignedMainTeacherId}::lead`}>{teachersById.get(selectedEvent.extendedProps.assignedMainTeacherId)?.displayName || 'Lead teacher'} — lead</option>}
                  {(selectedEvent.extendedProps?.assignedSupportingTeacherIds || []).map((teacherId: string) => <option key={`${teacherId}::co_teacher`} value={`${teacherId}::co_teacher`}>{teachersById.get(teacherId)?.displayName || 'Co-teacher'} — co-teacher</option>)}
                </select></label>
                <label>Reason<select value={adminSubReason} onChange={(event) => setAdminSubReason(event.target.value as SubRequestReason)} disabled={adminSubBusy}><option value="schedule_conflict">Schedule conflict</option><option value="illness">Illness</option><option value="travel">Travel</option><option value="other">Other</option></select></label>
                <label>Teaching notes<textarea rows={5} value={adminSubNote} onChange={(event) => setAdminSubNote(event.target.value)} placeholder="What should the substitute know to teach this class successfully?" disabled={adminSubBusy} /></label>
                <div className="calendar-admin-sub-form-actions"><button type="button" disabled={adminSubBusy} onClick={() => setAdminEventActionMode('actions')}>Back</button><button type="button" className="primary" disabled={adminSubBusy || !adminSubNote.trim()} onClick={() => void requestAdminSub()}>{adminSubBusy ? 'Requesting…' : 'Request sub'}</button></div>
              </div>
            )}
          </section>
        </div>
      )}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={() => {
          if (onEventDelete && selectedEvent) {
            onEventDelete(selectedEvent.id);
          }
          setIsDeleteModalOpen(false);
        }}
        title="Confirm Deletion"
        message={getPersonalizedMessage()}
      />
      <ConfirmationModal
        isOpen={isCancelSubConfirmOpen}
        onClose={() => { if (!adminSubBusy) setIsCancelSubConfirmOpen(false); }}
        onConfirm={() => void cancelAdminSub()}
        title="Cancel sub request?"
        message={`Cancel the sub request for ${selectedAdminEventTeacher?.displayName || 'this teacher'}?\n\nThe event will no longer appear as a substitute opportunity, and the availability change created by the request will be reversed.`}
        confirmText={adminSubBusy ? 'Canceling…' : 'Cancel sub request'}
        cancelText="Keep request"
      />
      <ConfirmationModal
        isOpen={isTzConfirmOpen}
        onClose={() => {
          setIsTzConfirmOpen(false);
          setPendingTimeZone(null);
        }}
        onConfirm={() => {
          const next = String(pendingTimeZone || '').trim();
          if (next) {
            setCalendarTimeZone(next);
            setHasManualTzOverride(next !== accountTimeZone);
          }
          setIsTzConfirmOpen(false);
          setPendingTimeZone(null);
        }}
        title="Switch calendar timezone?"
        message={`Are you sure you want to switch the calendar display timezone to:\n\n${TIMEZONE_OPTIONS.find(o => o.value === pendingTimeZone)?.label || pendingTimeZone}?\n\nThis only changes how times are displayed right now. The calendar will default back to your account timezone (${TIMEZONE_OPTIONS.find(o => o.value === accountTimeZone)?.label || accountTimeZone}) the next time you open it.`}
        confirmText="Switch timezone"
        cancelText="Keep current"
      />
      {discoveryPreview && (
        <div
          className="calendar-discovery-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setDiscoveryPreview(null);
          }}
        >
          <section className="calendar-discovery-modal" role="dialog" aria-modal="true" aria-labelledby="calendar-discovery-title">
            <button
              type="button"
              className="calendar-discovery-close"
              onClick={() => setDiscoveryPreview(null)}
              aria-label="Close squad preview"
            >
              ×
            </button>
            <span className="calendar-discovery-eyebrow">Nearby squad</span>
            <h2 id="calendar-discovery-title">{discoveryPreview.listing.title}</h2>
            <p className="calendar-discovery-location">
              {locationsById[discoveryPreview.listing.locationId || '']?.name
                || discoveryPreview.listing.locationName
                || discoveryPreview.listing.schoolName
                || 'Location to be announced'}
            </p>
            <dl>
              <div><dt>Program</dt><dd>{displayCurriculum(discoveryPreview.listing)}</dd></div>
              <div><dt>Season</dt><dd>{displaySeason(discoveryPreview.listing)}</dd></div>
              <div><dt>Dates</dt><dd>{formatListingDateRange(discoveryPreview.listing)}</dd></div>
              <div><dt>Time</dt><dd>{formatListingTime(discoveryPreview.listing)}</dd></div>
            </dl>
            {listingNeedsStaffing(discoveryPreview.listing) && (
              <p className="calendar-discovery-needed">This squad is looking for teachers.</p>
            )}
            <p className="calendar-discovery-note">
              This is a discovery preview. Classroom and student details remain private until you are assigned.
            </p>
          </section>
        </div>
      )}
      {onEventUpdate && (
        <EditEventModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={async (updatedEvent) => {
            const eventRef = doc(db, 'squadEvents', updatedEvent.id);
            try {
              const previousParticipants: EventParticipant[] = selectedEvent?.extendedProps?.eventParticipants || [];
              const nextParticipants: EventParticipant[] = updatedEvent.eventParticipants || [];
              const assignedMainTeacherId = selectedEvent?.extendedProps?.assignedMainTeacherId ?? selectedEvent?.extendedProps?.mainTeacherId ?? '';
              const assignedSupportingTeacherIds: string[] = selectedEvent?.extendedProps?.assignedSupportingTeacherIds ?? selectedEvent?.extendedProps?.supportingTeacherIds ?? [];
              const staffingChanged = String(updatedEvent.mainTeacherId || '') !== String(assignedMainTeacherId || '')
                || JSON.stringify([...(updatedEvent.supportingTeacherIds || [])].sort()) !== JSON.stringify([...assignedSupportingTeacherIds].sort());
              const hasActiveCoverage = Array.isArray(selectedEvent?.extendedProps?.activeCoverage)
                && selectedEvent.extendedProps.activeCoverage.length > 0;
              if (hasActiveCoverage && (String(updatedEvent.mainTeacherId || '') !== String(assignedMainTeacherId || '')
                || assignedSupportingTeacherIds.some(id => isSupportingAssignmentCovered(id, selectedEvent?.extendedProps?.activeCoverage) && !(updatedEvent.supportingTeacherIds || []).includes(id)))) {
                throw new Error('You can add supporting people during substitute coverage. Resolve coverage before replacing the lead or removing a teacher involved in the substitution.');
              }
              const batch = writeBatch(db);
              batch.update(eventRef, {
                title: updatedEvent.title,
                start: updatedEvent.start,
                end: updatedEvent.end,
                ...(hasActiveCoverage ? {} : { mainTeacherId: updatedEvent.mainTeacherId }),
                supportingTeacherIds: updatedEvent.supportingTeacherIds,
                ...(userData?.isAdmin ? { supportingStaff: updatedEvent.supportingStaff || {} } : {}),
                ...(!hasActiveCoverage && staffingChanged ? { mainTeacherOverridden: true } : {}),
              });

              // Add teacher to squad if not already a member
              if (updatedEvent.squadId) {
                const squad = squadsById.get(updatedEvent.squadId);
                const involvedTeacherIds = Array.from(new Set([
                  updatedEvent.mainTeacherId,
                  ...(updatedEvent.supportingTeacherIds || []),
                ].filter(Boolean)));
                const missingAccessIds = involvedTeacherIds.filter(id => !squad?.teacherIds?.includes(id));
                if (squad && missingAccessIds.length > 0) {
                  const squadRef = doc(db, 'squads', updatedEvent.squadId);
                  batch.update(squadRef, {
                    teacherIds: arrayUnion(...missingAccessIds)
                  });
                }
              }
              await batch.commit();
              if (updatedEvent.squadId) {
                const previousIds = new Set(previousParticipants.map(item => item.userId));
                const nextIds = new Set(nextParticipants.map(item => item.userId));
                await Promise.all([
                  ...nextParticipants.filter(item => !previousIds.has(item.userId)).map(participant => addEventParticipant({ eventId: updatedEvent.id, squadId: updatedEvent.squadId, participant })),
                  ...previousParticipants.filter(item => !nextIds.has(item.userId)).map(participant => removeEventParticipant({ eventId: updatedEvent.id, squadId: updatedEvent.squadId, userId: participant.userId })),
                ]);
              }
              
              // Optionally, refresh events from Firestore to ensure UI is in sync
              fetchEvents();

            } catch (error: any) {
              console.error("Error updating event:", error);
              throw error;
            }
            setIsEditModalOpen(false);
          }}
          event={selectedEvent}
          teachers={calendarTeachers}
          canEditSupportingStaff={userData?.isAdmin === true}
          regularLeadTeacherId={squadsById.get(selectedEvent?.extendedProps?.squadId)?.regularTeacher || squadsById.get(selectedEvent?.extendedProps?.squadId)?.leadTeacherId}
          eventTimeZone={squadsById.get(selectedEvent?.extendedProps?.squadId)?.timezone || DEFAULT_TZ}
          onFillSubRequest={userData?.isAdmin === true && selectedEvent?.extendedProps?.squadId ? async (substituteTeacherId: string) => {
            await claimSubOpportunity({
              eventId: selectedEvent.id,
              squadId: selectedEvent.extendedProps.squadId,
              claimerTeacherId: substituteTeacherId,
              substituteTeacherId,
            });
            fetchEvents();
          } : undefined}
        />
      )}
    </div>
  );
};

export default SquadCalendar;
